import React, { useEffect, useState } from 'react';
import { Table, Modal, Form, Button, Badge, Spinner, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import client from '../api/client';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';
import { fmtMoney, fmtDate, fmtNum } from '../utils/format';

const empty = {
  project: '',
  unitNo: '',
  unitType: '',
  floor: '',
  tower: '',
  customerName: '',
  customerPhone: '',
  customerEmail: '',
  customerAddress: '',
  salesAgent: '',
  areaSqft: 0,
  bookingRatePerSqft: 0,
  actualRatePerSqft: 0,
  otherCharges: 0,
  discount: 0,
  commissionType: 'percent',
  commissionValue: 0,
  bookingDate: new Date().toISOString().slice(0, 10),
  notes: '',
};

const Bookings = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: projs }] = await Promise.all([
        client.get('/bookings', { params: q ? { q } : {} }),
        client.get('/projects'),
      ]);
      setItems(data);
      setProjects(projs);
      if (user.role !== 'sales') {
        try {
          const { data: u } = await client.get('/users', { params: { role: 'sales' } });
          setAgents(u);
        } catch {}
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setEditing(null); setForm(empty); setShow(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      ...empty,
      ...it,
      project: it.project?._id || it.project || '',
      salesAgent: it.salesAgent?._id || it.salesAgent || '',
      bookingDate: it.bookingDate ? it.bookingDate.slice(0, 10) : '',
    });
    setShow(true);
  };
  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onProjectPick = (id) => {
    const p = projects.find((x) => x._id === id);
    setForm((f) => ({
      ...f,
      project: id,
      bookingRatePerSqft: f.bookingRatePerSqft || (p?.bookingRatePerSqft ?? 0),
      actualRatePerSqft: f.actualRatePerSqft || (p?.actualRatePerSqft ?? 0),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.salesAgent) delete payload.salesAgent;
    try {
      if (editing) {
        await client.put(`/bookings/${editing._id}`, payload);
        toast.success('Booking updated');
      } else {
        await client.post('/bookings', payload);
        toast.success('Booking created');
      }
      setShow(false);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.message || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    await client.delete(`/bookings/${id}`);
    toast.info('Booking deleted');
    load();
  };

  const totalBooking = (form.areaSqft || 0) * (form.bookingRatePerSqft || 0);
  const totalActual = (form.areaSqft || 0) * (form.actualRatePerSqft || 0);
  const grand = totalBooking + (form.otherCharges || 0) - (form.discount || 0);

  return (
    <div>
      <PageHeader title="Bookings / Sales" subtitle="Flat sales tracker" actionLabel="New Booking" onAction={openNew} />

      <InputGroup className="mb-3" style={{ maxWidth: 360 }}>
        <Form.Control placeholder="Search booking #, customer, unit" value={q} onChange={(e) => setQ(e.target.value)} />
        <Button onClick={load}><i className="bi bi-search"></i></Button>
      </InputGroup>

      {loading ? <Spinner animation="border" /> : (
        <div className="table-responsive">
          <Table hover>
            <thead>
              <tr>
                <th>Booking #</th><th>Project / Unit</th><th>Customer</th>
                <th>Area</th><th>Booking Rate</th><th>Total</th>
                <th>Received</th><th>Pending</th><th>Due</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={10} className="text-center text-muted py-4">No bookings</td></tr>
              )}
              {items.map((b) => (
                <tr key={b._id}>
                  <td><Link to={`/bookings/${b._id}`}>{b.bookingNo}</Link><br />
                    <Badge bg="secondary">{b.status}</Badge></td>
                  <td><strong>{b.project?.name}</strong><br /><small className="text-muted">Unit {b.unitNo}</small></td>
                  <td>{b.customerName}<br /><small className="text-muted">{b.customerPhone}</small></td>
                  <td>{fmtNum(b.areaSqft)} sqft</td>
                  <td>{fmtMoney(b.bookingRatePerSqft)}</td>
                  <td>{fmtMoney(b.grandTotal)}</td>
                  <td className="text-success">{fmtMoney(b.paymentTotalReceived)}</td>
                  <td className="text-warning">{fmtMoney(b.paymentPending)}</td>
                  <td className={b.paymentDue > 0 ? 'text-danger' : ''}>{fmtMoney(b.paymentDue)}</td>
                  <td className="text-end">
                    <Link className="btn btn-sm btn-outline-primary" to={`/bookings/${b._id}`}><i className="bi bi-eye"></i></Link>{' '}
                    {['admin', 'sales', 'support'].includes(user.role) && (
                      <Button size="sm" variant="outline-secondary" onClick={() => openEdit(b)}><i className="bi bi-pencil"></i></Button>
                    )}{' '}
                    {user.role === 'admin' && (
                      <Button size="sm" variant="outline-danger" onClick={() => remove(b._id)}><i className="bi bi-trash"></i></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal show={show} onHide={() => setShow(false)} size="xl">
        <Form onSubmit={save}>
          <Modal.Header closeButton><Modal.Title>{editing ? 'Edit Booking' : 'New Booking'}</Modal.Title></Modal.Header>
          <Modal.Body>
            <h6 className="text-muted">Property</h6>
            <div className="row g-3 mb-3">
              <div className="col-md-4"><Form.Label>Project *</Form.Label>
                <Form.Select required value={form.project} onChange={(e) => onProjectPick(e.target.value)}>
                  <option value="">-</option>
                  {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                </Form.Select></div>
              <div className="col-md-2"><Form.Label>Unit No *</Form.Label>
                <Form.Control required value={form.unitNo} onChange={(e) => onChange('unitNo', e.target.value)} /></div>
              <div className="col-md-2"><Form.Label>Tower</Form.Label>
                <Form.Control value={form.tower} onChange={(e) => onChange('tower', e.target.value)} /></div>
              <div className="col-md-2"><Form.Label>Floor</Form.Label>
                <Form.Control value={form.floor} onChange={(e) => onChange('floor', e.target.value)} /></div>
              <div className="col-md-2"><Form.Label>Unit Type</Form.Label>
                <Form.Control placeholder="2BHK" value={form.unitType} onChange={(e) => onChange('unitType', e.target.value)} /></div>
              <div className="col-md-3"><Form.Label>Area (sqft) *</Form.Label>
                <Form.Control type="number" required value={form.areaSqft} onChange={(e) => onChange('areaSqft', Number(e.target.value))} /></div>
              <div className="col-md-3"><Form.Label>Booking Rate (₹/sqft) *</Form.Label>
                <Form.Control type="number" required value={form.bookingRatePerSqft} onChange={(e) => onChange('bookingRatePerSqft', Number(e.target.value))} /></div>
              <div className="col-md-3"><Form.Label>Actual Rate (₹/sqft) *</Form.Label>
                <Form.Control type="number" required value={form.actualRatePerSqft} onChange={(e) => onChange('actualRatePerSqft', Number(e.target.value))} /></div>
              <div className="col-md-3"><Form.Label>Booking Date</Form.Label>
                <Form.Control type="date" value={form.bookingDate} onChange={(e) => onChange('bookingDate', e.target.value)} /></div>
              <div className="col-md-4"><Form.Label>Other Charges</Form.Label>
                <Form.Control type="number" value={form.otherCharges} onChange={(e) => onChange('otherCharges', Number(e.target.value))} /></div>
              <div className="col-md-4"><Form.Label>Discount</Form.Label>
                <Form.Control type="number" value={form.discount} onChange={(e) => onChange('discount', Number(e.target.value))} /></div>
              <div className="col-md-4 d-flex align-items-end">
                <div className="bg-light rounded p-2 w-100 small">
                  Total Booking Value: <strong>{fmtMoney(totalBooking)}</strong><br />
                  Total Actual Value: <strong>{fmtMoney(totalActual)}</strong><br />
                  Grand Total: <strong className="text-primary">{fmtMoney(grand)}</strong>
                </div>
              </div>
            </div>

            <h6 className="text-muted">Customer</h6>
            <div className="row g-3 mb-3">
              <div className="col-md-4"><Form.Label>Name *</Form.Label>
                <Form.Control required value={form.customerName} onChange={(e) => onChange('customerName', e.target.value)} /></div>
              <div className="col-md-3"><Form.Label>Phone *</Form.Label>
                <Form.Control required value={form.customerPhone} onChange={(e) => onChange('customerPhone', e.target.value)} /></div>
              <div className="col-md-5"><Form.Label>Email</Form.Label>
                <Form.Control type="email" value={form.customerEmail} onChange={(e) => onChange('customerEmail', e.target.value)} /></div>
              <div className="col-12"><Form.Label>Address</Form.Label>
                <Form.Control as="textarea" rows={2} value={form.customerAddress} onChange={(e) => onChange('customerAddress', e.target.value)} /></div>
            </div>

            <h6 className="text-muted">Sales Agent &amp; Commission</h6>
            <div className="row g-3 mb-3">
              {user.role !== 'sales' && (
                <div className="col-md-4"><Form.Label>Sales Agent *</Form.Label>
                  <Form.Select required value={form.salesAgent} onChange={(e) => onChange('salesAgent', e.target.value)}>
                    <option value="">-</option>
                    {agents.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
                  </Form.Select></div>
              )}
              <div className="col-md-3"><Form.Label>Commission Type</Form.Label>
                <Form.Select value={form.commissionType} onChange={(e) => onChange('commissionType', e.target.value)}>
                  <option value="percent">% of Booking Value</option>
                  <option value="per_sqft">₹ per sqft</option>
                  <option value="flat">Flat amount</option>
                </Form.Select></div>
              <div className="col-md-3"><Form.Label>Commission Value</Form.Label>
                <Form.Control type="number" step="0.01" value={form.commissionValue} onChange={(e) => onChange('commissionValue', Number(e.target.value))} /></div>
              <div className="col-md-2 d-flex align-items-end">
                <div className="bg-light rounded p-2 small w-100">
                  Calc: <strong>
                    {form.commissionType === 'percent'
                      ? fmtMoney((totalBooking * (form.commissionValue || 0)) / 100)
                      : form.commissionType === 'per_sqft'
                      ? fmtMoney((form.areaSqft || 0) * (form.commissionValue || 0))
                      : fmtMoney(form.commissionValue)}
                  </strong>
                </div>
              </div>
            </div>

            <Form.Label>Notes</Form.Label>
            <Form.Control as="textarea" rows={2} value={form.notes} onChange={(e) => onChange('notes', e.target.value)} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShow(false)}>Cancel</Button>
            <Button type="submit" variant="primary">{editing ? 'Save' : 'Create Booking'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Bookings;
